// src/components/cookies/CookieConsent.jsx
import * as React from 'react';
import {
  Box, Paper, Stack, Typography, Button, Switch, Dialog, DialogTitle,
  DialogContent, DialogActions, Divider
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/SafetyCheck';
import BarChartIcon from '@mui/icons-material/BarChart';
import SpeedIcon from '@mui/icons-material/Speed';
import CampaignIcon from '@mui/icons-material/Campaign';

import { useI18n } from '../../i18n/I18nProvider';
import cookieConsentI18n from '../../i18n/components/cookies/cookieconsent.i18n';

const IS_COLLECT_HOST = typeof window !== 'undefined' && window.location.hostname.toLowerCase().startsWith('collect.');
const GREEN = IS_COLLECT_HOST ? '#197348' : '#0466c8';
const GREEN_BG = IS_COLLECT_HOST ? 'rgba(25,115,72,0.09)' : 'rgba(11,95,255,0.08)';
const HOVER = IS_COLLECT_HOST ? '#105c37' : '#3b3e8c';
const COOKIE_KEY = 'cookie_prefs_v1';

/** Catégories — analytics activé par défaut */
const CATS_META = [
  { key: 'essential',   icon: <SecurityIcon />,  locked: true,  def: true  },
  { key: 'analytics',   icon: <BarChartIcon />,  locked: false, def: true },  // ✅ actif sans clic
  { key: 'performance', icon: <SpeedIcon />,     locked: false, def: false },
  { key: 'marketing',   icon: <CampaignIcon />,  locked: false, def: false },
];

/* ---------------- Stockage prefs ---------------- */
function loadPrefs() {
  try {
    const x = JSON.parse(localStorage.getItem(COOKIE_KEY) || 'null');
    if (x) return x;
  } catch {}
  const init = {};
  CATS_META.forEach(c => { init[c.key] = !!c.def; });
  return init;
}
function savePrefs(p) {
  localStorage.setItem(COOKIE_KEY, JSON.stringify(p || {}));
}

/* ---------------- GA helpers ---------------- */
function bootstrapDataLayerIfNeeded() {
  if (!window.dataLayer) window.dataLayer = [];
  if (!window.gtag) {
    window.gtag = function gtag(){ window.dataLayer.push(arguments); };
  }
}

function ensureGA(id) {
  if (!id || window.__gaLoaded) return;
  // charge gtag.js
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(s);

  bootstrapDataLayerIfNeeded();
  window.gtag('js', new Date());
  // on laisse la config à ton initGA() global, donc pas de page_view auto ici
  window.__gaLoaded = true;
}

function setDefaultGrantedOnce() {
  if (window.__defaultConsentSet) return;
  bootstrapDataLayerIfNeeded();
  window.gtag('consent', 'default', {
    analytics_storage: 'granted',   // ✅ analytics OK sans clic
    ad_storage:        'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage:  'granted',
    ad_user_data:      'denied',
    ad_personalization:'denied',
  });
  window.__defaultConsentSet = true;
}

function updateConsentFromPrefs(prefs) {
  bootstrapDataLayerIfNeeded();
  window.gtag('consent', 'update', {
    analytics_storage: prefs.analytics ? 'granted' : 'denied',
    ad_storage:        prefs.marketing ? 'granted' : 'denied',
    // tu peux propager performance → functionality_storage si tu veux
    functionality_storage: prefs.performance ? 'granted' : 'denied',
  });
}

/* ---------------- ID GA (CRA + Vite) ---------------- */
const GA_ID =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_GA_MEASUREMENT_ID) ||
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_GA_MEASUREMENT_ID) ||
  '';

export default function CookieConsent() {
  const { t, register } = useI18n();
  React.useEffect(() => { register('cookies', cookieConsentI18n); }, [register]);

  const [prefs, setPrefs] = React.useState(loadPrefs);
  const [showBanner, setShowBanner] = React.useState(() => !localStorage.getItem(COOKIE_KEY));
  const [openDlg, setOpenDlg] = React.useState(false);

  /** AU MONTAGE :
   *  - on met consent default = granted (analytics)
   *  - on charge gtag.js immédiatement
   *  - on applique les prefs existantes (si déjà enregistrées)
   */
  React.useEffect(() => {
    setDefaultGrantedOnce();
    if (GA_ID) ensureGA(GA_ID);
    updateConsentFromPrefs({ ...prefs, essential: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ouvre la modale depuis n’importe où
  React.useEffect(() => {
    const h = () => setOpenDlg(true);
    window.addEventListener('openCookiePrefs', h);
    return () => window.removeEventListener('openCookiePrefs', h);
  }, []);

  const setOne = (k, v) => setPrefs(p => ({ ...p, [k]: v }));

  const apply = (next) => {
    next = { ...next, essential: true };
    setPrefs(next);
    savePrefs(next);
    if (GA_ID && next.analytics) ensureGA(GA_ID);
    updateConsentFromPrefs(next);
  };

  const acceptAll = () => {
    const next = {}; CATS_META.forEach(c => next[c.key] = true);
    apply(next);
    setShowBanner(false);
    setOpenDlg(false);
  };

  const rejectNonEssential = () => {
    const next = {}; CATS_META.forEach(c => next[c.key] = !!c.locked);
    apply(next);
    setShowBanner(false);
    setOpenDlg(false);
  };

  const savePrefsAndClose = () => {
    apply(prefs);
    setShowBanner(false);
    setOpenDlg(false);
  };

  // Libellés i18n
  const CATS = React.useMemo(
    () => CATS_META.map(c => ({
      ...c,
      label: t(`cookies.consent.cats.${c.key}.label`),
      desc:  t(`cookies.consent.cats.${c.key}.desc`),
    })),
    [t]
  );

  return (
    <>
      {/* Bannière */}
      {showBanner && (
        <Box
          sx={{
            position: 'fixed', bottom: 12, left: 12, right: 12, zIndex: 1300,
            display: 'flex', justifyContent: 'center', pointerEvents: 'none'
          }}
        >
          <Paper
            elevation={6}
            sx={{
              pointerEvents: 'auto',
              maxWidth: 960, width: '100%',
              p: { xs: 1.5, md: 2 },
              borderRadius: 3, border: '1px solid #e8efe9', background: '#fff'
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 800, mb: 0.25 }}>
                  {t('cookies.consent.banner.title')}
                </Typography>
                <Typography sx={{ color: 'text.secondary' }}>
                  {t('cookies.consent.banner.text')}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ minWidth: { md: 360 } }}>
                <Button
                  variant="outlined"
                  onClick={rejectNonEssential}
                  sx={{ textTransform: 'none', borderRadius: 2, borderColor: GREEN, color: GREEN,
                        '&:hover': { borderColor: HOVER, color: HOVER } }}
                >
                  {t('cookies.consent.actions.reject')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => setOpenDlg(true)}
                  sx={{ textTransform: 'none', borderRadius: 2, borderColor: GREEN, color: GREEN,
                        '&:hover': { borderColor: HOVER, color: HOVER } }}
                >
                  {t('cookies.consent.actions.settings')}
                </Button>
                <Button
                  variant="contained"
                  onClick={acceptAll}
                  sx={{ textTransform: 'none', borderRadius: 2, bgcolor: GREEN,
                        '&:hover': { bgcolor: HOVER } }}
                >
                  {t('cookies.consent.actions.accept')}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        </Box>
      )}

      {/* Dialog de préférences */}
      <Dialog open={openDlg} onClose={() => setOpenDlg(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900 }}>
          {t('cookies.consent.dialog.title')}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 1.5 }}>
          <Typography sx={{ color: 'text.secondary', mb: 1.5 }}>
            {t('cookies.consent.dialog.intro')}
          </Typography>
          <Stack spacing={1.25}>
            {CATS.map(c => {
              const on = !!prefs[c.key];
              return (
                <Paper key={c.key} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ bgcolor: GREEN_BG, color: GREEN, borderRadius: 2,
                              width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {c.icon}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 800 }}>{c.label}</Typography>
                      <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>{c.desc}</Typography>
                      {c.key === 'analytics' && (
                        <Typography sx={{ color: 'text.secondary', fontSize: 12, mt: 0.25 }}>
                          {t('cookies.consent.dialog.gaLine')}{' '}
                          <code>
                            {
                              (typeof process !== 'undefined' && process.env && process.env.REACT_APP_GA_MEASUREMENT_ID) ||
                              (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_GA_MEASUREMENT_ID) ||
                              t('cookies.consent.dialog.gaUnset')
                            }
                          </code>
                        </Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {on ? t('cookies.consent.switch.on') : t('cookies.consent.switch.off')}
                      </Typography>
                      <Switch color="success" checked={on} disabled={c.locked} onChange={(_,v)=>setOne(c.key, v)} />
                    </Stack>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {t('cookies.consent.dialog.footer')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 1.5 }}>
          <Button
            variant="outlined"
            onClick={rejectNonEssential}
            sx={{ textTransform: 'none', borderRadius: 2, borderColor: GREEN, color: GREEN,
                  '&:hover': { borderColor: HOVER, color: HOVER } }}
          >
            {t('cookies.consent.actions.reject')}
          </Button>
          <Button
            variant="outlined"
            onClick={acceptAll}
            sx={{ textTransform: 'none', borderRadius: 2, borderColor: GREEN, color: GREEN,
                  '&:hover': { borderColor: HOVER, color: HOVER } }}
          >
            {t('cookies.consent.actions.acceptAll')}
          </Button>
          <Button
            variant="contained"
            onClick={savePrefsAndClose}
            sx={{ textTransform: 'none', borderRadius: 2, bgcolor: GREEN,
                  '&:hover': { bgcolor: HOVER } }}
          >
            {t('cookies.consent.actions.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** Utilitaire global : ouvrir le panneau depuis n’importe où */
export function openCookiePreferences() {
  window.dispatchEvent(new Event('openCookiePrefs'));
}
